import { useMemo, useState } from 'react';
import { useLocalStorageByUser } from 'common/hooks';
import { CALENDAR_COLOR_PALETTE_KEY, CALENDAR_COLOR_PRESETS_KEY, CalendarColorPalette, DEFAULT_CALENDAR_COLOR_PALETTE, MODELS_CALENDAR_EVENT_COLORS } from 'dashboard409a/common/constants/calendar';

export type ColorPalettePreset = {
  name: string;
  palette: CalendarColorPalette;
};

let cachedCalendarColorPalette: CalendarColorPalette = DEFAULT_CALENDAR_COLOR_PALETTE;

export const getCalendarColorPalette = (): CalendarColorPalette => cachedCalendarColorPalette;

// Resolve status colors from the active user's hydrated palette cache.
// Note: Status 5 is now COMPLETE. The WAITING status is deprecated and handled via
// the is_waiting boolean field. Any existing records with the old model_status=5 (WAITING)
// have been migrated to use is_waiting=True with model_status=0 (DRAFT).
export const getStatusColor = (status: number): string => {
  const palette = getCalendarColorPalette();
  const statusMap: Record<number, keyof CalendarColorPalette> = {
    0: 'DRAFT',
    1: 'REVIEW',
    2: 'IN_PROCESS_SCALAR_USA',
    3: 'FINAL',
    4: 'REVIEW_FINAL',
    5: 'COMPLETE',
  };
  return palette[statusMap[status]] || MODELS_CALENDAR_EVENT_COLORS.DEFAULT;
};

export const saveColorPalettePreset = (
  name: string,
  palette: CalendarColorPalette,
  presets: ColorPalettePreset[],
  setPresets: (value: ColorPalettePreset[]) => void
): void => {
  const existingIndex = presets.findIndex(p => p.name === name);
  const updated =
    existingIndex >= 0
      ? presets.map((p, idx) => (idx === existingIndex ? { name, palette } : p))
      : [...presets, { name, palette }];
  setPresets(updated);
};

export const deleteColorPalettePreset = (
  name: string,
  presets: ColorPalettePreset[],
  setPresets: (value: ColorPalettePreset[]) => void
): void => {
  setPresets(presets.filter(p => p.name !== name));
};

/**
 * Custom hook for managing color palette state and operations
 * @returns Object containing colorPalette, presets, and handler functions
 */
export const useColorPalette = () => {
  const [storedColorPalette, setColorPalette, isReady] = useLocalStorageByUser<CalendarColorPalette>(
    CALENDAR_COLOR_PALETTE_KEY,
    DEFAULT_CALENDAR_COLOR_PALETTE
  );
  const colorPalette = useMemo(
    () => {
      const next = { ...DEFAULT_CALENDAR_COLOR_PALETTE, ...storedColorPalette };
      // Update synchronously during render so non-hook consumers (e.g. styled components
      // calling getCalendarColorPalette()) see the current palette before any child renders.
      cachedCalendarColorPalette = next;
      return next;
    },
    [storedColorPalette]
  );
  const [presets, setPresets] = useLocalStorageByUser<ColorPalettePreset[]>(
    CALENDAR_COLOR_PRESETS_KEY,
    []
  );
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  const handleColorChange = (key: keyof CalendarColorPalette, color: string) => {
    setColorPalette(prev => {
      const next = { ...prev, [key]: color };
      cachedCalendarColorPalette = next;
      return next;
    });
  };

  const handleReset = () => {
    cachedCalendarColorPalette = DEFAULT_CALENDAR_COLOR_PALETTE;
    setColorPalette(DEFAULT_CALENDAR_COLOR_PALETTE);
  };

  const handleSavePreset = () => {
    if (presetName.trim()) {
      saveColorPalettePreset(presetName.trim(), colorPalette, presets, setPresets);
      setPresetName('');
      setSavePresetOpen(false);
    }
  };

  const handleOpenSavePreset = () => {
    setSavePresetOpen(true);
  };

  const handleCloseSavePreset = () => {
    setSavePresetOpen(false);
    setPresetName('');
  };

  const handleApplyPreset = (name: string) => {
    const preset = presets.find(p => p.name === name);

    if (preset) {
      setColorPalette(preset.palette);
      cachedCalendarColorPalette = preset.palette;
    }
  };

  const handleDeletePreset = (name: string) => {
    deleteColorPalettePreset(name, presets, setPresets);
  };

  return {
    colorPalette,
    isReady,
    presets,
    savePresetOpen,
    presetName,
    setPresetName,
    handleColorChange,
    handleReset,
    handleSavePreset,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleApplyPreset,
    handleDeletePreset,
  };
};
